// ============================================================
// LoginPage — Pantalla de login (rediseño split-screen)
// Panel izquierdo: hero institucional con gradiente
// Panel derecho: formulario centrado
// ============================================================

import React, { useEffect, useState } from 'react';
import { useLogin } from 'hooks/UseLogin';
import { getConfig } from 'utils/appConfig';
import { consumeLogoutReason } from 'contexts/AuthContext';
import { LogoutReason } from 'utils/httpClient';
import { I } from 'components/ui';

const LOGOUT_REASON_MESSAGE: Record<LogoutReason, string> = {
  'session-replaced':
    'Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo. Por favor vuelve a iniciar sesión.',
  'invalid-token':
    'Tu sesión ya no es válida. Por favor vuelve a iniciar sesión.',
  'session-lost':
    'Tu sesión expiró. Por favor vuelve a iniciar sesión.',
};

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { form, error, isSubmitting, handleChange, handleSubmit } = useLogin();
  const institucion = getConfig().institucionNombre || 'Institución Educativa';

  // Si el último logout fue forzado (otra sesión, token inválido, etc.),
  // el AuthContext persistió la razón en sessionStorage. La consumimos una
  // sola vez al montar para mostrar un banner informativo.
  const [logoutBanner, setLogoutBanner] = useState<string | null>(null);
  useEffect(() => {
    const reason = consumeLogoutReason();
    if (reason) setLogoutBanner(LOGOUT_REASON_MESSAGE[reason]);
  }, []);

  return (
    <div
      className="sae-login min-h-screen grid"
      style={{ gridTemplateColumns: '1fr 1fr', background: 'var(--bg)' }}
    >
      {/* ── Panel izquierdo: hero ──────────────────────── */}
      <div
        className="sae-login-art relative overflow-hidden flex flex-col justify-between p-12 text-white"
        style={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 50%, #0d9488 100%)',
        }}
      >
        {/* Glows de fondo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 20% 80%, rgba(20,184,166,.4) 0%, transparent 40%), radial-gradient(circle at 80% 20%, rgba(244,114,182,.25) 0%, transparent 40%)',
          }}
        />

        {/* Top: Brand mark */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div
              className="rounded-r grid place-items-center text-white font-extrabold"
              style={{
                width: 44,
                height: 44,
                fontSize: 17,
                background: 'linear-gradient(135deg, #14b8a6, #4f46e5)',
                boxShadow: '0 4px 12px -2px rgba(20,184,166,.4)',
              }}
            >
              SAE
            </div>
            <div>
              <div className="text-[18px] font-extrabold leading-tight" style={{ letterSpacing: '-.01em' }}>
                SAE Educativo
              </div>
              <div className="text-[12px] opacity-70">
                Plataforma integral de gestión escolar
              </div>
            </div>
          </div>
        </div>

        {/* Centro: Mensaje */}
        <div className="relative">
          <h1
            className="text-[38px] font-extrabold m-0 mb-4 max-w-[460px]"
            style={{ letterSpacing: '-.03em', lineHeight: 1.1 }}
          >
            Una sola plataforma para administrar toda tu institución.
          </h1>
          <p className="text-[15px] opacity-85 leading-relaxed max-w-[440px] m-0">
            Matrícula, asistencia, notas, cobros y comunicación con familias —
            coordinados en tiempo real.
          </p>

          <div className="flex gap-6 mt-9 flex-wrap">
            <div>
              <div className="text-[26px] font-extrabold" style={{ letterSpacing: '-.02em' }}>+1,240</div>
              <div className="text-[12px] opacity-70 uppercase mt-0.5" style={{ letterSpacing: '.06em' }}>
                Estudiantes activos
              </div>
            </div>
            <div>
              <div className="text-[26px] font-extrabold" style={{ letterSpacing: '-.02em' }}>98.2%</div>
              <div className="text-[12px] opacity-70 uppercase mt-0.5" style={{ letterSpacing: '.06em' }}>
                Asistencia mensual
              </div>
            </div>
            <div>
              <div className="text-[26px] font-extrabold" style={{ letterSpacing: '-.02em' }}>24/7</div>
              <div className="text-[12px] opacity-70 uppercase mt-0.5" style={{ letterSpacing: '.06em' }}>
                Soporte técnico
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: copyright */}
        <div className="relative text-[12px] opacity-55">
          © {new Date().getFullYear()} SAE — {institucion} · Lima, Perú
        </div>
      </div>

      {/* ── Panel derecho: formulario ──────────────────── */}
      <div className="sae-login-form flex items-center justify-center p-10">
        <div className="w-full max-w-[400px]">
          <h2 className="text-[26px] font-extrabold m-0 mb-1.5 text-ink-900" style={{ letterSpacing: '-.02em' }}>
            Bienvenido de vuelta
          </h2>
          <p className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--primary-700)' }}>
            {institucion}
          </p>
          <p className="text-ink-500 text-[14px] m-0 mb-7">
            Ingresa con tu cuenta institucional para continuar.
          </p>

          {logoutBanner && (
            <div
              role="status"
              className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-r px-3.5 py-2.5 mb-4"
            >
              <I.AlertCircle size={16} style={{ color: '#b45309', marginTop: 2, flexShrink: 0 }} />
              <div className="flex-1 text-[13px]" style={{ color: '#92400e' }}>
                {logoutBanner}
              </div>
              <button
                type="button"
                onClick={() => setLogoutBanner(null)}
                aria-label="Cerrar aviso"
                style={{ background: 'transparent', border: 'none', padding: 0, color: '#92400e', cursor: 'pointer' }}
              >
                <I.X size={14} />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="grid gap-3.5">
            {/* Identifier */}
            <div className="field">
              <label htmlFor="identifier">Usuario o correo</label>
              <div className="relative">
                <I.User
                  size={16}
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--ink-400)',
                  }}
                />
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  value={form.identifier}
                  onChange={handleChange}
                  placeholder="Usuario o correo electrónico"
                  disabled={isSubmitting}
                  className="input"
                  style={{ paddingLeft: 38 }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="field">
              <div className="flex justify-between items-center">
                <label htmlFor="password">Contraseña</label>
                <a
                  href="mailto:soporte@adamsmith.edu.pe"
                  className="text-[11px] font-semibold no-underline"
                  style={{ color: 'var(--primary-700)' }}
                >
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
              <div className="relative">
                <I.Lock
                  size={16}
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--ink-400)',
                  }}
                />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Ingrese su contraseña"
                  disabled={isSubmitting}
                  className="input"
                  style={{ paddingLeft: 38, paddingRight: 38 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                  style={{ background: 'transparent', border: 'none', padding: 0 }}
                >
                  {showPassword ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-r px-3.5 py-2.5"
              >
                <I.AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <span className="text-[13px]" style={{ color: '#991b1b' }}>{error}</span>
              </div>
            )}

            {/* Mantener sesión */}
            <label className="flex items-center gap-2 text-[13px] text-ink-700 mt-1">
              <input type="checkbox" defaultChecked /> Mantener sesión iniciada
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary mt-1 justify-center"
              style={{ padding: 12 }}
            >
              {isSubmitting ? (
                <>
                  <SpinnerIcon /> <span>Verificando...</span>
                </>
              ) : (
                <>
                  Iniciar Sesión
                  <I.ChevronRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-[12px] text-ink-500 mt-6">
            ¿Necesitas ayuda?{' '}
            <a
              href="mailto:soporte@adamsmith.edu.pe"
              className="font-semibold no-underline"
              style={{ color: 'var(--primary-700)' }}
            >
              Contacta soporte
            </a>
          </p>

          <p className="text-center text-[11px] text-ink-400 mt-3">
            Powered by{' '}
            <a
              href="https://www.saeperu.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold no-underline hover:underline"
              style={{ color: 'var(--primary-600)' }}
            >
              saeperu.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
